import {
	Button,
	Col,
	DatePicker,
	Popover,
	Row,
	Select,
	Switch,
} from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { Navbar } from "../Navigation/Navbar";
import { StatTileV2 } from "../StatTileV2/StatTileV2";
import { CompactStatTile } from "../StatTileV2/CompactStatTile";
import { useState } from "react";
import { trainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { getTeamOverviewDefaults, setTeamPresets } from "../../store/actions/team.actions";
import { LoadingComponent } from "../Common/LoadingComponent";
import moment from "moment";
import { DATE_FORMAT } from "../../Utils/date_utils";

import BarChart from '../../Components/Charts/BarChart';
import { mapDataForOverviewV2Chart } from '../../mappers/model/BarChartMapper';
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";

export const OverviewV2 = () => {
	const PRESET_TYPE = PresetType.Overview;
	const DEFAULT_PRESET = 'Default'

	const dispatch = useDispatch();
	const {
		overviewStats,
		misc: { comparison_types },
		dropdowns: {player: playerDropdowns},
		loading: { overviewStats: loading },
		all_metric_types,
		presets,
	} = useSelector(({ team }) => team);

	const {
		user,
	  } = useAuth0();

	const [metricKeys, setMetricKeys] = useState(new Array(8).fill(""));
	const [stat, setStat] = useState("Avg");
	const [option, setOption] = useState("latest_vs_prev");
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)
	const [pending, setPending] = useState(false);
	const [startDate, setStartDate] = useState(moment().subtract(1, "month"));
	const [endDate, setEndDate] = useState(moment());
	const [showGameDays, setShowGameDays] = useState(false);
	const [showWeekly, setShowWeekly] = useState(false);
	const [loadingChart, setLoadingChart] = useState(false);
	const [chartData, setChartData] = useState({ data: [], metrics: [] });
	const [unsavedChanges, setUnsavedChanges] = useState(false)
	
	useMetricDropdowns(['testing', 'forceplate', 'gps', 'wellness']);

	useEffect(() => {
		dispatch(getTeamOverviewDefaults());
		fetchChartData();
	}, []);

	const handleChangeMetricKeys = (index) => (value) => {
		const newMetricKeys = [...metricKeys];
		newMetricKeys[index] = value;
		setMetricKeys(newMetricKeys);

		setUnsavedChanges(true)
	};

	const saveTiles = async () => {
		setPending(true);
		try {
			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateOverviewDefaultMetrics(metricKeys);
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: metricKeys, preset_id: presetModified.preset_id
				});
				presetModified.config = metricKeys
				dispatch(setTeamPresets(presets))
			}
			message.success("Saved tiles");
		} catch (e) {
			message.error("Error saving tiles");
		}
		setPending(false);
	};

	const handleChangeDateRange = ([startDate, endDate]) => {
		setStartDate(startDate);
		setEndDate(endDate);
	};

	const fetchChartData = async () => {
		setLoadingChart(true);
		try {
			const data = await trainingAPI.getOverviewChartV2({
				start_date: startDate.format(DATE_FORMAT),
				end_date: endDate.format(DATE_FORMAT),
				weekly: showWeekly,
				game_days_only: showGameDays,
			});
			setChartData(data);
		} catch (e) {}
		setLoadingChart(false);
	}

	return (
		<>
			<Navbar />
			<div
				style={{
					margin: "1rem",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: 16,
				}}
			>
				<DatePicker.RangePicker
					value={[startDate, endDate]}
					onChange={handleChangeDateRange}
				/>
				<Popover
					content={
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								gap: 16,
								width: 140,
							}}
						>
							<Switch
								checkedChildren="Game Days"
								unCheckedChildren="All Days"
								value={showGameDays}
								onChange={(val) => setShowGameDays(val)}
							/>
						</div>
					}
					title="Day Options"
					trigger="click"
				>
					<Button>Day Options</Button>
				</Popover>
				<Button
					type="primary"
					onClick={fetchChartData}
					loading={loadingChart}
				>
					Search
				</Button>
			</div>
			<LoadingComponent loading={loadingChart} title="Loading chart...">
				<div style={{display: 'flex', justifyContent: 'center'}}>
					<div style={{width: '95%'}}>
					{
						chartData && chartData?.metrics?.length && (
							<BarChart 
								options={mapDataForOverviewV2Chart(chartData)}
								height={280}
							/>
						)
					}
				</div>
			</div>
			</LoadingComponent>
			<LoadingComponent loading={loading} title="Loading metrics...">
				<div
					style={{
						minHeight: "100%",
						margin: "1rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<div className="exportable tab-testing testing-stats testing-stats-toggles flex gap-16 mb-16 items-center w-full justify-center">
						<PresetSelection 
							presets={presets} 
							user={user} 
							presetType={PRESET_TYPE} 
							statDefaults={overviewStats}
							currStatMetrics={metricKeys}
							setStatMetrics={setMetricKeys} 
							currentSelectedTilePreset={currentSelectedTilePreset}
							setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
							comparisonTypes={comparison_types}
							comparisonOption={option}
							setComparisonOption={setOption}
							handleSave={saveTiles}
							warnUnsavedChanges={unsavedChanges}
							setWarnUnsavedChages={setUnsavedChanges}
							/>
					</div>

					<Row
						gutter={[8, 12]}
						style={{ width: "90%"}}
						className="exportable tab-testing testing-stats"
					>
						{new Array(8).fill(0).map((_, i) => (
							<Col span={6} key={i} style={{ display: 'flex', justifyContent: 'center' }}>
								<CompactStatTile
									dropdowns={playerDropdowns}
									metric_identifier_with_stat={metricKeys[i]}
									onChange={handleChangeMetricKeys(i)}
									option={option}
									endDate={endDate?.format(DATE_FORMAT)}
									startDate={startDate?.format(DATE_FORMAT)}
									isCompact={true}
									type={"team"}
									comparison={option}
								/>

							</Col>
						))}
					</Row>
				</div>
			</LoadingComponent>
		</>
	);
};
